import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "./api";
import { getTenantSlug } from "./tenantSlug";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = unknown/loading
  const [ready, setReady] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
    } catch {
      setUser(false);
    } finally {
      setReady(true);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.access_token) localStorage.setItem("sellerhub_token", data.access_token);
      setUser(data.user);
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  };

  const register = async (payload) => {
    try {
      // Attribute the sign-up to the tenant whose portal URL they used, so a
      // client of agent X never lands in agent Y's (or the buyer beta) tenant.
      const { data } = await api.post("/auth/register", { ...payload, agent_slug: getTenantSlug() });
      if (data.access_token) localStorage.setItem("sellerhub_token", data.access_token);
      setUser(data.user);
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    localStorage.removeItem("sellerhub_token");
    localStorage.removeItem("sellerhub_admin_token_backup");
    setUser(false);
  };

  const impersonate = async (sellerId) => {
    try {
      const adminToken = localStorage.getItem("sellerhub_token");
      const { data } = await api.post(`/admin/seller/${sellerId}/impersonate`);
      if (adminToken) localStorage.setItem("sellerhub_admin_token_backup", adminToken);
      localStorage.setItem("sellerhub_token", data.access_token);
      setUser(data.user);
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  };

  const exitImpersonation = async () => {
    const backup = localStorage.getItem("sellerhub_admin_token_backup");
    if (!backup) {
      await logout();
      return;
    }
    localStorage.setItem("sellerhub_token", backup);
    localStorage.removeItem("sellerhub_admin_token_backup");
    await refresh();
  };

  const updateProfile = async (patch) => {
    try {
      const { data } = await api.patch("/auth/profile", patch);
      setUser(data.user);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) };
    }
  };

  // Buy/sell portal — flip which transaction the seller-portal UI is
  // currently showing. Backend rejects sides the user isn't linked to.
  const switchSide = async (side) => {
    try {
      const { data } = await api.post("/auth/active-side", { side });
      setUser(data.user);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) };
    }
  };

  return (
    <AuthContext.Provider value={{ user, ready, login, register, logout, refresh, updateProfile, impersonate, exitImpersonation, switchSide }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
